// Actions cycle de vie — rendues sur chaque UnifiedLeadCard.
// Charge le schema de transitions depuis /leads-engine/lifecycle/schema
// (côté parent) et n'affiche que les statuts atteignables. Persistance
// obligatoire — jamais d'état local isolé.
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  NAVY, CHAMPAGNE, IVORY, GOLD,
} from "@/components/altiora/PremiumUI";
import {
  TRANSITION_BUTTON_LABELS, humanStatus, statusPillStyle,
} from "@/lib/altiora-leads-i18n";
import CloseReasonModal from "./CloseReasonModal";

// Fallback en cas d'absence de schema : dérivé de la version backend
const FALLBACK_TRANSITIONS = {
  NEW:              ["QUALIFIED", "TO_REVIEW", "IGNORED"],
  QUALIFIED:        ["TO_REVIEW", "READY_TO_CONTACT", "IGNORED"],
  TO_REVIEW:        ["READY_TO_CONTACT", "IGNORED", "QUALIFIED"],
  READY_TO_CONTACT: ["CONTACTED", "IGNORED", "TO_REVIEW"],
  CONTACTED:        ["FOLLOW_UP", "WON", "LOST", "IGNORED"],
  FOLLOW_UP:        ["CONTACTED", "WON", "LOST", "IGNORED"],
  WON:              [],
  LOST:             ["FOLLOW_UP"],
  IGNORED:          ["NEW", "TO_REVIEW"],
};

export default function LifecycleActions({
  lead, record, transitionsSchema, onChange, onCrmSync, onUpsert,
}) {
  const [busy, setBusy] = useState(false);
  const [closeModal, setCloseModal] = useState(null); // {toStatus}
  const status = record?.status || "NEW";
  const allowed = (transitionsSchema && transitionsSchema[status])
                    || FALLBACK_TRANSITIONS[status] || [];
  const style = statusPillStyle(status);

  // Persistance : garantir que le lead existe côté backend avant
  // toute transition (l'user peut cliquer directement depuis une
  // card qui n'est pas encore trackée)
  const ensurePersisted = async () => {
    if (record) return true;
    try {
      const r = await api.post("/leads-engine/leads/upsert",
                                     { unified: lead });
      onUpsert?.(r.data.record);
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail
                   || "Impossible de persister ce lead.";
      toast.error(msg);
      return false;
    }
  };

  const doTransition = async (to, extra = {}) => {
    const ok = await ensurePersisted();
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/leads-engine/leads/${encodeURIComponent(lead.entity_id)}/status`,
        { to_status: to, ...extra });
      onChange?.(r.data.record, r.data.next_best_action);
      toast.success(`Statut : ${humanStatus(to)}`);
    } catch (err) {
      const msg = err?.response?.data?.detail
                   || "Transition refusée.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const handleClick = (to) => {
    if (to === "IGNORED" || to === "LOST") {
      setCloseModal({ toStatus: to });
    } else {
      doTransition(to);
    }
  };

  const handleAddToCrm = async () => {
    const ok = await ensurePersisted();
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/leads-engine/leads/${encodeURIComponent(lead.entity_id)}/crm-sync`);
      onCrmSync?.(r.data);
      if (r.data.created)
        toast.success("Ajouté au CRM ALTIORA.");
      else if (r.data.enriched)
        toast.success("Relation existante enrichie par ALTIORA Leads.");
    } catch (err) {
      const msg = err?.response?.data?.detail
                   || "Échec du transfert vers le CRM.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const crmSynced = !!record?.crm_target_id;

  return (
    <div data-testid={`lifecycle-actions-${lead.entity_id}`}
         className="flex flex-wrap items-center gap-2 pt-3"
         style={{ borderTop: `1px dashed ${CHAMPAGNE}55` }}>
      <span data-testid="lifecycle-current-status"
            className="text-[10px] font-mono uppercase tracking-[0.22em] px-2 py-1 rounded-full"
            style={{ background: style.bg, color: style.color }}>
        {humanStatus(status)}
      </span>

      {allowed.map((to) => (
        <button key={to}
                data-testid={`lifecycle-btn-${to}`}
                onClick={() => handleClick(to)}
                disabled={busy}
                className="text-[11px] px-2.5 py-1 rounded-full transition-all"
                style={{ background: "transparent", color: NAVY,
                         border: `1px solid ${CHAMPAGNE}88` }}>
          {TRANSITION_BUTTON_LABELS[to] || to}
        </button>
      ))}

      {!crmSynced ? (
        <button
          data-testid="lifecycle-btn-crm-sync"
          onClick={handleAddToCrm}
          disabled={busy}
          className="text-[11px] px-2.5 py-1 rounded-full transition-all ml-auto"
          style={{ background: NAVY, color: IVORY }}>
          + Ajouter au CRM
        </button>
      ) : (
        <div className="flex items-center gap-2 ml-auto">
          <span data-testid="lifecycle-crm-synced"
                className="text-[11px] px-2.5 py-1 rounded-full"
                style={{ background: `${GOLD}33`, color: NAVY }}>
            ✓ Dans le CRM
          </span>
          <button
            data-testid="lifecycle-btn-crm-enrich"
            onClick={handleAddToCrm}
            disabled={busy}
            title="Repousser les données ALTIORA les plus récentes vers la fiche CRM"
            className="text-[11px] px-2.5 py-1 rounded-full transition-all"
            style={{ background: "transparent", color: NAVY,
                     border: `1px solid ${CHAMPAGNE}88` }}>
            Enrichir la fiche
          </button>
        </div>
      )}

      {closeModal && (
        <CloseReasonModal
          toStatus={closeModal.toStatus}
          onConfirm={({ reason, comment }) => {
            doTransition(closeModal.toStatus,
                             { close_reason: reason, comment });
            setCloseModal(null);
          }}
          onCancel={() => setCloseModal(null)} />
      )}
    </div>
  );
}
